import React, { useEffect, useState } from "react";
import { X, Save, Calculator } from "lucide-react";
import { TreasuryEntry } from "../../../../services/treasuryReportingService";

interface Props {
  isOpen: boolean;
  editingEntry: TreasuryEntry | null;
  onClose: () => void;
  onSave: (data: any) => void;
}

const INITIAL = {
  report_date: new Date().toISOString().split("T")[0],
  capital_start: 0,
  daily_volume: 0,
  daily_revenue: 0,
  daily_expenses: 0,
  bank_balance: 0,
  mtn_balance: 0,
  crypto_available: 0,
  comment: "",
};

const TreasuryEntryModal: React.FC<Props> = ({ isOpen, editingEntry, onClose, onSave }) => {
  const [form, setForm] = useState(INITIAL);
  const [computed, setComputed] = useState({ result: 0, financial_stock: 0, gap: 0 });

  useEffect(() => {
    if (editingEntry) {
      setForm({
        report_date: editingEntry.report_date,
        capital_start: editingEntry.capital_start,
        daily_volume: editingEntry.daily_volume,
        daily_revenue: editingEntry.daily_revenue,
        daily_expenses: editingEntry.daily_expenses,
        bank_balance: editingEntry.bank_balance,
        mtn_balance: editingEntry.mtn_balance,
        crypto_available: editingEntry.crypto_available,
        comment: editingEntry.comment || "",
      });
    } else {
      setForm(INITIAL);
    }
  }, [editingEntry, isOpen]);

  useEffect(() => {
    const result = form.capital_start + form.daily_revenue - form.daily_expenses;
    const financial_stock = form.bank_balance + form.mtn_balance + form.crypto_available;
    const gap = result - financial_stock;
    setComputed({ result, financial_stock, gap });
  }, [form]);

  if (!isOpen) return null;

  const handleChange = (field: string, value: string | number) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    onSave(form);
  };

  const inputClass =
    "w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500 outline-none";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto m-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-800">
            {editingEntry ? "Modifier l'entrée" : "Nouvelle entrée"} — Reporting Trésorerie
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
            <input
              type="date"
              value={form.report_date}
              onChange={(e) => handleChange("report_date", e.target.value)}
              className={inputClass}
            />
          </div>

          {/* Inputs row 1 */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Capital de départ</label>
              <input
                type="number"
                step="0.01"
                value={form.capital_start}
                onChange={(e) => handleChange("capital_start", parseFloat(e.target.value) || 0)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Volume transaction</label>
              <input
                type="number"
                step="0.01"
                value={form.daily_volume}
                onChange={(e) => handleChange("daily_volume", parseFloat(e.target.value) || 0)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Revenu du jour</label>
              <input
                type="number"
                step="0.01"
                value={form.daily_revenue}
                onChange={(e) => handleChange("daily_revenue", parseFloat(e.target.value) || 0)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Dépenses du jour</label>
              <input
                type="number"
                step="0.01"
                value={form.daily_expenses}
                onChange={(e) => handleChange("daily_expenses", parseFloat(e.target.value) || 0)}
                className={inputClass}
              />
            </div>
          </div>

          {/* Inputs row 2 */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Solde Bank (CAD)</label>
              <input
                type="number"
                step="0.01"
                value={form.bank_balance}
                onChange={(e) => handleChange("bank_balance", parseFloat(e.target.value) || 0)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Solde MTN (CAD)</label>
              <input
                type="number"
                step="0.01"
                value={form.mtn_balance}
                onChange={(e) => handleChange("mtn_balance", parseFloat(e.target.value) || 0)}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Crypto disponible (CAD)</label>
              <input
                type="number"
                step="0.01"
                value={form.crypto_available}
                onChange={(e) => handleChange("crypto_available", parseFloat(e.target.value) || 0)}
                className={inputClass}
              />
            </div>
          </div>

          {/* Comment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Commentaire</label>
            <textarea
              rows={2}
              value={form.comment}
              onChange={(e) => handleChange("comment", e.target.value)}
              className={inputClass}
              placeholder="Commentaire optionnel..."
            />
          </div>

          {/* Computed preview */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <Calculator size={16} />
              <span>Aperçu des calculs</span>
            </div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Résultat =</span>
                <div className="font-mono font-semibold text-lg">{computed.result.toFixed(2)}</div>
                <div className="text-xs text-gray-400">Capital + Revenu - Dépenses</div>
              </div>
              <div>
                <span className="text-gray-500">Stock financier =</span>
                <div className="font-mono font-semibold text-lg">{computed.financial_stock.toFixed(2)}</div>
                <div className="text-xs text-gray-400">Bank + MTN + Crypto</div>
              </div>
              <div>
                <span className="text-gray-500">Écart =</span>
                <div
                  className={`font-mono font-semibold text-lg ${computed.gap >= 0 ? "text-green-600" : "text-red-600"}`}
                >
                  {computed.gap.toFixed(2)}
                </div>
                <div className="text-xs text-gray-400">Résultat - Stock</div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-white border rounded-lg hover:bg-gray-50"
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-green-600 rounded-lg hover:bg-green-700"
          >
            <Save size={16} />
            {editingEntry ? "Mettre à jour" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TreasuryEntryModal;
